import type { Decorator } from "@storybook/react";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

interface UserStageContextType {
    userStage: string | null;
    setUserStage: (stage: string) => void;
    logout: () => void;
}

export const UserStageContext = createContext<UserStageContextType | undefined>(
    undefined,
);

export const UserStageProvider = ({ children }: { children: ReactNode }) => {
    const [userStage, setUserStageState] = useState<string | null>(null);

    useEffect(() => {
        const storedStage = localStorage.getItem("userStage");
        if (storedStage) {
            setUserStageState(storedStage);
        }
    }, []);

    const setUserStage = (stage: string) => {
        setUserStageState(stage);
        localStorage.setItem("userStage", stage);
    };

    const logout = () => {
        localStorage.removeItem("userStage");
        setUserStageState(null);
    };

    return (
        <UserStageContext.Provider
            value={{
                userStage,
                setUserStage,
                logout,
            }}
        >
            {children}
        </UserStageContext.Provider>
    );
};

export const useUserStage = () => {
    const context = useContext(UserStageContext);
    if (!context) {
        throw new Error(
            "useUserStage must be used within an UserStageProvider",
        );
    }
    return context;
};
export const UserStageDecorator: Decorator = (story, context) => {
    const Story = story;
    return (
        <UserStageContext.Provider
            value={{
                logout: () => undefined,
                setUserStage: () => undefined,
                userStage: "",
            }}
        >
            <Story {...context} />
        </UserStageContext.Provider>
    );
};
